import type { IngestionSourceSummary, NewIngestionKeyResult } from '@jums-dashboard/shared';
import { useMemo, useState } from 'react';

import Badge from '@/components/Badge/Badge';
import Button from '@/components/Button/Button';
import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import Switch from '@/components/Switch/Switch';

import useCurrentHome from '../hooks/useCurrentHome';
import useDeleteIngestionKey from '../hooks/useDeleteIngestionKey';
import useIngestionSources from '../hooks/useIngestionSources';
import useRegenerateIngestionKey from '../hooks/useRegenerateIngestionKey';
import useSetIngestionKeyEnabled from '../hooks/useSetIngestionKeyEnabled';
import { formatDateMs } from '../lib/format';
import AsyncSection from './AsyncSection';
import NewIngestionKeyModal from './NewIngestionKeyModal';

export default function IngestionUrlsSection() {
  const homeQuery = useCurrentHome();
  const homeId = homeQuery.data?.id;
  const list = useIngestionSources(homeId);
  const [showNew, setShowNew] = useState(false);
  const [revealResult, setRevealResult] = useState<NewIngestionKeyResult | null>(null);

  const loading = homeQuery.isPending || list.isPending;
  const error = homeQuery.isError || !homeId || list.isError;
  const errorMessage = homeQuery.isError || !homeId ? 'Failed to load home.' : 'Failed to load ingestion keys.';
  const retry = () => {
    if (homeQuery.isError) void homeQuery.refetch();
    if (list.isError) void list.refetch();
  };

  const items = list.data ?? [];
  const takenSourceIds = useMemo(() => items.filter((i) => i.enabled).map((i) => i.sourceId), [items]);

  const closeModal = () => {
    setShowNew(false);
    setRevealResult(null);
  };

  return (
    <AsyncSection loading={loading} error={error} errorMessage={errorMessage} onRetry={retry}>
      {homeId && (
        <div className="space-y-4">
          <p className="text-sm text-(--text-secondary)">
            Generate single-use URLs for sensor sources. Disable any URL to immediately block ingestion.
          </p>
          <div className="flex justify-center">
            <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
              New ingestion key
            </Button>
          </div>

          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-(--border-default) bg-(--bg-surface) px-4 py-6 text-center text-sm text-(--text-secondary)">
              No ingestion keys yet. Create one to start receiving sensor data.
            </div>
          ) : (
            <ul className="divide-y divide-(--border-subtle) overflow-hidden rounded-lg border border-(--border-subtle) bg-(--bg-surface)">
              {items.map((item) => (
                <IngestionKeyRow
                  key={item.keyHash}
                  item={item}
                  homeId={homeId}
                  onRegenerated={(res) => {
                    setRevealResult(res);
                    setShowNew(true);
                  }}
                />
              ))}
            </ul>
          )}

          <NewIngestionKeyModal
            open={showNew}
            homeId={homeId}
            onClose={closeModal}
            takenSourceIds={takenSourceIds}
            initialResult={revealResult}
          />
        </div>
      )}
    </AsyncSection>
  );
}

interface RowProps {
  item: IngestionSourceSummary;
  homeId: string;
  onRegenerated: (res: NewIngestionKeyResult) => void;
}

type Pending = 'regenerate' | 'delete' | null;

function IngestionKeyRow({ item, homeId, onRegenerated }: RowProps) {
  const toggle = useSetIngestionKeyEnabled();
  const regenerate = useRegenerateIngestionKey();
  const remove = useDeleteIngestionKey();
  const [pending, setPending] = useState<Pending>(null);
  const busy = toggle.isPending || regenerate.isPending || remove.isPending;

  const confirmRegenerate = () => {
    if (regenerate.isPending) return;
    regenerate.mutate(
      { homeId, keyHash: item.keyHash },
      {
        onSuccess: (res) => {
          setPending(null);
          onRegenerated(res);
        },
        onError: () => setPending(null),
      },
    );
  };

  const confirmDelete = () => {
    if (remove.isPending) return;
    remove.mutate(
      { homeId, keyHash: item.keyHash },
      {
        onSuccess: () => setPending(null),
        onError: () => setPending(null),
      },
    );
  };

  return (
    <li className="flex flex-col gap-3 px-4 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-(--text-primary)">{item.label}</span>
            <Badge tone={item.enabled ? 'success' : 'neutral'}>{item.enabled ? 'enabled' : 'disabled'}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--text-tertiary)">
            <span>
              {item.source} · <span className="font-mono">{item.sourceId}</span>
            </span>
            <span>received {item.receivedCount}</span>
            <span>last used {formatDateMs(item.lastUsedAtMs)}</span>
          </div>
        </div>
        <div className="shrink-0">
          <Switch
            label={item.enabled ? 'Enabled' : 'Disabled'}
            hideLabel
            checked={item.enabled}
            disabled={busy}
            onChange={(next) => toggle.mutate({ homeId, keyHash: item.keyHash, enabled: next })}
          />
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setPending('regenerate')}
          loading={regenerate.isPending}
          disabled={busy && !regenerate.isPending}
        >
          Regenerate URL
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => setPending('delete')}
          loading={remove.isPending}
          disabled={busy && !remove.isPending}
        >
          Delete
        </Button>
      </div>

      <ConfirmModal
        open={pending === 'regenerate'}
        title="Regenerate ingestion URL?"
        body={
          <>
            Regenerate the URL for <strong className="text-(--text-primary)">{item.label}</strong>? The old URL will stop working
            immediately and any device still using it will fail to ingest.
          </>
        }
        confirmLabel="Regenerate"
        loading={regenerate.isPending}
        onConfirm={confirmRegenerate}
        onCancel={() => {
          if (!regenerate.isPending) setPending(null);
        }}
      />
      <ConfirmModal
        open={pending === 'delete'}
        title="Delete ingestion key?"
        body={
          <>
            Delete ingestion key <strong className="text-(--text-primary)">{item.label}</strong>? This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!remove.isPending) setPending(null);
        }}
      />
    </li>
  );
}
